import {
  formatDate,
  getSupportContactLink,
  type SurveyState,
  type TFunction,
  useSecureGraphqlQuery,
} from '@psyomics/ui-common';
import {
  Badge,
  ContentCard,
  GenericError,
  Header,
  Illustration,
  type IllustrationName,
  LoadingSpinner,
  Text,
} from '@psyomics/ui-components';
import { ChevronRight, CircleArrowRight, CircleCheck } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { GetAllAvailableContentDocument, GetAllAvailableContentQuery } from '../../content/generated/content';
import { GetServiceUserInvitesQuery, Invite, ReferralSessionStatus, ReferralStatus } from '../generated/invites';
import { useServiceUserInvites } from '../hooks/use-service-user-invites';

const images: IllustrationName[] = ['WomanPuzzle', 'BrainHealth', 'HeartInHands'] as const;
const TWO = 2;
const randomImages = [...images].sort(() => Math.random() * TWO - 1);

interface GetSurveyStateProps {
  hasAnswers: boolean;
  isCompleted: boolean;
  referralCancelled: boolean;
  referralSessionExpired: boolean;
}

const getSurveyState = ({
  hasAnswers,
  isCompleted,
  referralCancelled,
  referralSessionExpired,
}: GetSurveyStateProps): SurveyState => {
  if (referralSessionExpired) return 'expired';
  if (referralCancelled) return 'cancelled';
  if (isCompleted) return 'completed';
  if (hasAnswers) return 'inProgress';
  return 'notStarted';
};

const getStateIcon = (state: SurveyState) => {
  if (state === 'completed') return <CircleCheck size={18} />;
  if (state === 'notStarted' || state === 'inProgress') return <CircleArrowRight size={18} />;

  return undefined;
};

const getCheckInContentCardProps = (
  invite: GetServiceUserInvitesQuery['getServiceUserInvites']['invites'][number],
  t: TFunction
) => {
  const { description: title } = invite.referralSession.referral.service;
  const { contact } = invite.referralSession.referral;
  const { supportContact, supportHref } = getSupportContactLink(contact);
  const referralCancelled = invite.referralSession.referral.status === ReferralStatus.Cancelled;
  const referralSessionExpired = invite.referralSession.status === ReferralSessionStatus.Expired;
  const isCompleted = Boolean(invite.surveyResponse?.completedAt);
  const isDisabled = referralCancelled || referralSessionExpired || (isCompleted && !invite.reviewEnabled);

  const state = getSurveyState({
    hasAnswers: Boolean(invite.surveyResponse?.answers),
    isCompleted,
    referralCancelled,
    referralSessionExpired,
  });

  const stateIcon = getStateIcon(state);

  const stateMessage = t(`page.home.cardMessage.${state}`);
  const sentMessage = t('page.home.cardMessage.sent');
  const sentDate = formatDate(new Date(invite.createdAt));
  const description = `${stateMessage}, ${sentMessage} ${sentDate}`;

  const supportInformation = (state === 'cancelled' || state === 'expired') && (
    <Trans
      components={{
        supportLink: (
          <a className="underline" href={supportHref}>
            {supportContact}
          </a>
        ),
      }}
      i18nKey="page.home.cardMessage.contactSupport"
    />
  );

  return { description, isDisabled, stateIcon, supportInformation, title };
};

function sortInvites(invitations: Invite[]) {
  return invitations.sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)));
}

const getQuestionnaireRoute = (invite: Invite) =>
  `/questionnaire/${invite.publicId}${invite.reviewEnabled && invite.status === 'completed' ? '/review' : ''}`;

const HealthLibraryCard = () => {
  const { t } = useTranslation();

  return (
    <div className="relative">
      <Badge
        className="pointer-events-none absolute top-2 right-2 z-10 bg-chartreuse-400 text-typography border-0"
        size="lg"
      >
        {t('page.home.healthLibraryCard.badge')}
      </Badge>
      <Link className="flex flex-row items-center gap-3 primary-card-link p-3" to="/content">
        <Illustration className="shrink-0" name="HeartBrain" size={100} />
        <div className="flex flex-col flex-1 min-w-0">
          <Header level="h2">{t('page.healthLibrary.title')}</Header>
          <Text size="lg">{t('page.home.healthLibraryCard.description')}</Text>
        </div>
        <ChevronRight className="shrink-0" size={28} />
      </Link>
    </div>
  );
};

type CheckInData = {
  readonly contentFetching: boolean;
  readonly hasContent: boolean;
  readonly invites: Invite[];
  readonly invitesFetching: boolean;
};

const hasQuestionnaires = ({ invites }: Pick<CheckInData, 'invites'>) => invites.length > 0;

const hasRenderableContent = (data: CheckInData) => data.hasContent || hasQuestionnaires(data);

const isLoadingInitialContent = (data: CheckInData) =>
  !hasRenderableContent(data) && (data.invitesFetching || data.contentFetching);

const NoActiveReferralError = () => (
  <GenericError
    ellipse1Variant="indigoPurple"
    illustration="WomanEmptyBox"
    subtitleKey="page.home.noActiveReferral.subtitle"
    titleKey="page.home.noActiveReferral.title"
  />
);

type QuestionnairesSectionProps = {
  readonly invites: Invite[];
  readonly invitesFetching: boolean;
  readonly onInviteClick: (invite: Invite) => void;
};

const QuestionnairesSection = ({ invites, invitesFetching, onInviteClick }: QuestionnairesSectionProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Header className="mt-3" level="h2">
        {t('page.home.questionnaires.header')}
      </Header>
      {invitesFetching ? (
        <LoadingSpinner />
      ) : (
        invites.map((invite, idx) => {
          const { description, isDisabled, stateIcon, supportInformation, title } = getCheckInContentCardProps(
            invite,
            t
          );

          return (
            <div key={invite.publicId}>
              <ContentCard
                description={description}
                disabled={isDisabled}
                image={randomImages[idx % randomImages.length]}
                onClick={() => onInviteClick(invite)}
                stateIcon={stateIcon}
                supportInformation={supportInformation}
                title={title}
              />
            </div>
          );
        })
      )}
    </>
  );
};

const useCheckInData = (): CheckInData => {
  const { fetching: invitesFetching, data } = useServiceUserInvites();
  const [{ data: contentData, fetching: contentFetching }] = useSecureGraphqlQuery<GetAllAvailableContentQuery>({
    query: GetAllAvailableContentDocument,
  });
  const invites = data?.getServiceUserInvites.invites ?? [];
  const hasContent = Boolean(contentData?.getAllAvailableContent?.length);

  return {
    contentFetching,
    hasContent,
    invites,
    invitesFetching,
  };
};

const CheckInContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const checkInData = useCheckInData();
  const { hasContent, invites, invitesFetching } = checkInData;

  const onClick = (invite: Invite) => navigate(getQuestionnaireRoute(invite));

  if (isLoadingInitialContent(checkInData)) return <LoadingSpinner />;

  if (!invitesFetching && !hasQuestionnaires(checkInData)) return <NoActiveReferralError />;

  const sortedInvites = sortInvites(invites);
  const firstName = sortedInvites[0]?.givenName;

  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3">
      <Header className="mt-1" level="h1">
        {t('page.home.greeting')}
        {firstName ? ` ${firstName}` : ''},
      </Header>
      {hasContent && <HealthLibraryCard />}
      <QuestionnairesSection invites={sortedInvites} invitesFetching={invitesFetching} onInviteClick={onClick} />
    </div>
  );
};

export { CheckInContent, getSupportContactLink, getSurveyState };
